/**
 * Hook: useResourceLimitsAdmin
 * Descrição: Versão admin do useResourceLimits que recebe tenantId como parâmetro
 * 
 * Usado pelo Root Admin para visualizar limites de qualquer tenant
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação para Admin Tenant Management
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface EffectiveLimits {
  // Campos primários retornados pela RPC
  tenant_id: string;
  plan_id: string;
  plan_name: string;
  is_enterprise: boolean;
  // Limites efetivos (base + addons)
  effective_max_inboxes: number;
  effective_max_agents: number;
  effective_max_instances: number;
  effective_max_messages: number;
  // Limites base do plano
  base_max_inboxes: number;
  base_max_agents: number;
  base_max_instances: number;
  base_max_messages: number;
  // Quantidades adicionais dos addons
  addon_inboxes: number;
  addon_agents: number;
  addon_instances: number;
  addon_messages: number;
  // Módulos especiais
  has_campaigns_module: boolean;
  has_digital_contracts_module: boolean;
}

/**
 * Interface expandida com detalhes completos do add-on
 * Retornada pela RPC get_tenant_addon_details (Admin only)
 */
export interface TenantAddonDetails {
  id: string;
  addon_id: string;
  addon_name: string;
  addon_type: string;
  quantity: number;
  status: string;
  provider_id: string | null;
  provider_name: string | null;
  provider_slug: string | null;
  price_monthly: number;
  price_yearly: number | null;
  setup_fee: number;
  created_at: string;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_item_id: string | null;
  stripe_price_id: string | null;
  linked_inboxes: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    phone: string | null;
  }>;
}

export function useResourceLimitsAdmin(tenantId: string | null) {
  // Buscar limites efetivos via RPC
  const { data: limits, isLoading: limitsLoading } = useQuery({
    queryKey: ["admin-tenant-limits", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      logger.debug("useResourceLimitsAdmin", "Buscando limites do tenant", { tenantId });

      const { data, error } = await supabase.rpc("get_tenant_effective_limits", {
        p_tenant_id: tenantId,
      });

      if (error) {
        logger.error("useResourceLimitsAdmin", "Erro ao buscar limites", { error });
        throw error;
      }

      // RPC pode retornar array OU objeto dependendo da versão
      // Normalizar para sempre retornar o objeto de limites
      if (!data) {
        logger.warn("useResourceLimitsAdmin", "RPC retornou null/undefined", { tenantId });
        return null;
      }

      // Se for array, pegar primeiro item; se for objeto, usar diretamente
      const limitsData = Array.isArray(data) ? data[0] : data;
      
      logger.debug("useResourceLimitsAdmin", "Limites obtidos com sucesso", { 
        tenantId,
        isArray: Array.isArray(data),
        hasData: !!limitsData 
      });

      return (limitsData || null) as unknown as EffectiveLimits | null;
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Buscar add-ons com detalhes completos via RPC (Admin only)
  const { data: tenantAddons, isLoading: addonsLoading } = useQuery({
    queryKey: ["admin-tenant-addons-details", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug("useResourceLimitsAdmin", "Buscando detalhes dos add-ons via RPC", { tenantId });

      const { data, error } = await supabase.rpc("get_tenant_addon_details", {
        p_tenant_id: tenantId,
      });

      if (error) {
        logger.error("useResourceLimitsAdmin", "Erro ao buscar detalhes dos add-ons", { error });
        throw error;
      }

      logger.debug("useResourceLimitsAdmin", "Add-ons obtidos com sucesso", { 
        tenantId,
        count: data?.length || 0 
      });

      return (data || []) as TenantAddonDetails[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Derivar flags do plano - usar is_enterprise se disponível, fallback para plan_id
  const isEnterprise = limits?.is_enterprise ?? limits?.plan_id === "enterprise";
  const hasCampaignsModule = limits?.has_campaigns_module ?? false;
  const hasDigitalContractsModule = limits?.has_digital_contracts_module ?? false;

  return {
    limits,
    tenantAddons,
    isLoading: limitsLoading || addonsLoading,
    isEnterprise,
    hasCampaignsModule,
    hasDigitalContractsModule,
  };
}
